/* tarot-unlock-gate.js
 * Daily-reading gate for /pages/free-ai-tarot-online (AkashicStone).
 * Loaded AFTER free-ai-tarot-online.js so window.AKASHICSTONE_TAROT_CONFIG is available.
 *
 * Behaviour
 *  - 1 free reading per 24h (localStorage).
 *  - A blocked "Begin Your Free Reading" click opens a modal with 4 pieces.
 *  - The 4 pieces are the ones the app itself generated at the end of the LAST
 *    completed reading (the "Featured Spiritual Jewelry" block, driven by
 *    THEME_PRODUCTS[readingTheme]). They are snapshotted from the rendered
 *    recommendation block, so the gate never needs to know the React internals.
 *  - If no reading has been completed yet, the curated fallback list
 *    (window.AKASHICSTONE_TAROT_CONFIG.unlockProducts) is used instead.
 *  - Viewing one piece unlocks exactly one extra reading.
 */
(function () {
  "use strict";

  var DAY = 24 * 60 * 60 * 1000;
  var KEY_LAST = "ak_tarot_last_reading";
  var KEY_UNLOCK = "ak_tarot_unlocked_at";
  var KEY_RECO = "ak_tarot_last_reco";
  var START_LABEL = "Begin Your Free Reading";
  var DEFAULT_RECO_LABEL = "Featured Spiritual Jewelry";
  var COLLECTION_FALLBACK = "/collections/jewelry";

  var cfg = window.AKASHICSTONE_TAROT_CONFIG || {};
  var FALLBACK_PRODUCTS = (cfg.unlockProducts || []).slice(0, 4);

  /* ---------- storage (safe) ---------- */
  var store = (function () {
    try {
      var probe = "__ak_tg_probe__";
      window.localStorage.setItem(probe, "1");
      window.localStorage.removeItem(probe);
      return window.localStorage;
    } catch (e) {
      return null;
    }
  })();

  function num(key) {
    if (!store) return 0;
    var v = parseFloat(store.getItem(key));
    return isFinite(v) && v > 0 ? v : 0;
  }
  function put(key, val) {
    if (!store) return;
    try { store.setItem(key, String(val)); } catch (e) {}
  }
  function drop(key) {
    if (store) { try { store.removeItem(key); } catch (e) {} }
  }
  function readReco() {
    if (!store) return null;
    try {
      var obj = JSON.parse(store.getItem(KEY_RECO) || "null");
      if (obj && obj.items && obj.items.length) return obj;
    } catch (e) {}
    return null;
  }
  function writeReco(heading, items) {
    if (!store) return;
    try {
      store.setItem(KEY_RECO, JSON.stringify({ ts: Date.now(), heading: heading, items: items }));
    } catch (e) {}
  }

  /* ---------- snapshot the post-reading recommendation block ---------- */
  function text(el) {
    return (el && el.textContent ? el.textContent : "").replace(/\s+/g, " ").trim();
  }

  function snapshotReco() {
    try {
      var root = document.getElementById("tarot-app");
      /* Only snapshot after a reading has actually been generated. Without
         this guard the neutral/default block shown before a reading and
         after "New Reading" overwrites the last reading's themed picks. */
      if (!root || !root.querySelector(".reading-content")) return;
      var links = root.querySelectorAll(".product-title-link");
      if (!links.length) return;
      var items = [];
      for (var i = 0; i < links.length && items.length < 8; i++) {
        var a = links[i];
        var card = a.parentElement;
        if (!card) continue;
        var url = a.getAttribute("href") || "";
        if (!url) continue;
        var img = card.querySelector("img.product-image");
        var price = card.querySelector("p");
        items.push({
          url: url,
          title: text(a.querySelector(".product-title") || a),
          image: img ? (img.getAttribute("src") || "") : "",
          price: text(price)
        });
      }
      if (!items.length) return;

      var heading = "";
      var wrap = links[0].parentElement.parentElement.parentElement;
      if (wrap) {
        var h = wrap.querySelector("h2");
        if (h) heading = text(h);
      }
      writeReco(heading, items);
    } catch (e) {}
  }

  function watchRecommendation() {
    var root = document.getElementById("tarot-app");
    if (!root || !window.MutationObserver) return;
    var queued = false;
    var observer = new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        snapshotReco();
      }, 300);
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  /* ---------- gate logic ---------- */
  function nextFreeAt() {
    var last = num(KEY_LAST);
    return last ? last + DAY : 0;
  }

  function isAllowed() {
    var last = num(KEY_LAST);
    if (!last) return true;                       // first ever reading
    if (Date.now() - last >= DAY) return true;    // 24h window elapsed
    return num(KEY_UNLOCK) >= last;               // a piece was viewed since the last reading
  }

  function consume() {
    put(KEY_LAST, Date.now());
    drop(KEY_UNLOCK);
  }

  function normalize(el) {
    return (el && el.textContent ? el.textContent : "").replace(/\s+/g, " ").trim();
  }

  function startButtonFrom(target) {
    if (!target || target.nodeType !== 1 || !target.closest) return null;
    var btn = target.closest("button");
    if (!btn) return null;
    return normalize(btn) === START_LABEL ? btn : null;
  }

  function formatRemainder(ms) {
    if (ms < 0) ms = 0;
    var totalMin = Math.floor(ms / 60000);
    var h = Math.floor(totalMin / 60);
    var m = totalMin % 60;
    if (h <= 0 && m <= 0) return "less than a minute";
    var parts = [];
    if (h > 0) parts.push(h + (h === 1 ? " hour" : " hours"));
    if (m > 0) parts.push(m + (m === 1 ? " min" : " mins"));
    return parts.join(" ");
  }

  var bypassing = false;
  var blockedBtn = null;
  var overlay = null;
  var grid = null;
  var footer = null;
  var recoLabel = null;
  var timerLine = null;
  var tickId = null;
  var lastFocus = null;

  /* ---------- modal ---------- */
  function buildCard(item) {
    var a = document.createElement("a");
    a.className = "ak-tg-card";
    a.href = item.url || COLLECTION_FALLBACK;
    a.target = "_blank";
    a.rel = "noopener";

    var thumb = document.createElement("span");
    thumb.className = "ak-tg-thumb";
    if (item.image) {
      var img = document.createElement("img");
      img.className = "ak-tg-img";
      img.src = item.image;
      img.alt = item.title || "Recommended piece";
      img.loading = "lazy";
      img.decoding = "async";
      thumb.appendChild(img);
    }
    a.appendChild(thumb);

    var body = document.createElement("span");
    body.className = "ak-tg-body";

    var title = document.createElement("span");
    title.className = "ak-tg-name";
    title.textContent = item.title || "View piece";
    body.appendChild(title);

    var priceText = item.price ? (item.price.charAt(0) === "$" ? item.price : "$" + item.price) : "";
    if (priceText) {
      var price = document.createElement("span");
      price.className = "ak-tg-price";
      price.textContent = priceText;
      body.appendChild(price);
    }

    var cta = document.createElement("span");
    cta.className = "ak-tg-cta";
    cta.textContent = "View piece";
    body.appendChild(cta);

    a.appendChild(body);

    a.addEventListener("click", function () {
      put(KEY_UNLOCK, Date.now());
      renderFooter();
    });

    return a;
  }

  function renderProducts() {
    var reco = readReco();
    var items = reco && reco.items.length ? reco.items : FALLBACK_PRODUCTS;
    var label = reco && reco.heading ? reco.heading : DEFAULT_RECO_LABEL;

    if (recoLabel) recoLabel.textContent = label;
    grid.textContent = "";

    if (!items.length) {
      var link = document.createElement("a");
      link.className = "ak-tg-fallback";
      link.href = COLLECTION_FALLBACK;
      link.target = "_blank";
      link.rel = "noopener";
      link.textContent = "Browse the jewelry collection";
      link.addEventListener("click", function () {
        put(KEY_UNLOCK, Date.now());
        renderFooter();
      });
      grid.appendChild(link);
      return;
    }

    for (var i = 0; i < items.length; i++) {
      grid.appendChild(buildCard(items[i]));
    }
  }

  function renderFooter() {
    footer.textContent = "";
    if (isAllowed()) {
      var go = document.createElement("button");
      go.type = "button";
      go.className = "ak-tg-continue";
      go.textContent = "Continue Your Reading";
      go.addEventListener("click", continueReading);
      footer.appendChild(go);

      var hint = document.createElement("p");
      hint.className = "ak-tg-hint";
      hint.textContent = "Unlocked \u2014 tap to continue where you left off.";
      footer.appendChild(hint);
    } else {
      var tip = document.createElement("p");
      tip.className = "ak-tg-hint";
      tip.textContent = "Tap any piece above to unlock another reading.";
      footer.appendChild(tip);
    }
  }

  function updateTimer() {
    if (!timerLine) return;
    var remain = nextFreeAt() - Date.now();
    timerLine.textContent =
      "Your next free reading unlocks in " + formatRemainder(remain) +
      ", or instantly after viewing a piece below.";
  }

  function ensureModal() {
    if (overlay) return overlay;

    overlay = document.createElement("div");
    overlay.className = "ak-tg-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-labelledby", "ak-tg-title");

    var panel = document.createElement("div");
    panel.className = "ak-tg-panel";
    overlay.appendChild(panel);

    var close = document.createElement("button");
    close.type = "button";
    close.className = "ak-tg-close";
    close.setAttribute("aria-label", "Close");
    close.innerHTML = "&times;";
    close.addEventListener("click", closeModal);
    panel.appendChild(close);

    var head = document.createElement("header");
    head.className = "ak-tg-head";

    var kicker = document.createElement("p");
    kicker.className = "ak-tg-kicker";
    kicker.textContent = "Today's Free Reading";
    head.appendChild(kicker);

    var title = document.createElement("h2");
    title.className = "ak-tg-title";
    title.id = "ak-tg-title";
    title.textContent = "Today's reading is done \u2014 unlock more";
    head.appendChild(title);

    var sub = document.createElement("p");
    sub.className = "ak-tg-sub";
    sub.textContent =
      "You get one free reading every 24 hours. Browse any of the pieces below to unlock and continue your reading right away.";
    head.appendChild(sub);

    timerLine = document.createElement("p");
    timerLine.className = "ak-tg-timer";
    head.appendChild(timerLine);

    panel.appendChild(head);

    recoLabel = document.createElement("p");
    recoLabel.className = "ak-tg-reco";
    panel.appendChild(recoLabel);

    grid = document.createElement("div");
    grid.className = "ak-tg-grid";
    panel.appendChild(grid);

    footer = document.createElement("div");
    footer.className = "ak-tg-foot";
    panel.appendChild(footer);

    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeModal();
    });

    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && overlay && overlay.classList.contains("ak-tg-open")) {
        closeModal();
      }
    });

    document.body.appendChild(overlay);
    return overlay;
  }

  function openModal(trigger) {
    ensureModal();
    blockedBtn = trigger || blockedBtn;
    lastFocus = document.activeElement;
    renderProducts();
    renderFooter();
    updateTimer();
    overlay.classList.add("ak-tg-open");
    document.documentElement.classList.add("ak-tg-lock");
    if (tickId) clearInterval(tickId);
    tickId = setInterval(updateTimer, 30000);
    var closeBtn = overlay.querySelector(".ak-tg-close");
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    if (!overlay) return;
    overlay.classList.remove("ak-tg-open");
    document.documentElement.classList.remove("ak-tg-lock");
    if (tickId) { clearInterval(tickId); tickId = null; }
    if (lastFocus && lastFocus.focus) {
      try { lastFocus.focus(); } catch (e) {}
    }
  }

  function continueReading() {
    var btn = blockedBtn;
    closeModal();
    consume();
    if (btn && document.body.contains(btn)) {
      bypassing = true;
      try { btn.click(); } finally { bypassing = false; }
    }
  }

  /* ---------- interception ---------- */
  document.addEventListener(
    "click",
    function (e) {
      if (bypassing) return;
      var btn = startButtonFrom(e.target);
      if (!btn) return;
      if (isAllowed()) {
        consume();
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      blockedBtn = btn;
      openModal(btn);
    },
    true
  );

  watchRecommendation();
  snapshotReco();
})();
